import { useEffect, useState } from "react";
import { useSeoMeta } from '@unhead/react';

import { track, trackVisitOnce } from '@/lib/track';
import { useGame } from '@/game/useGame';
import { useHustle } from '@/game/hustle/useHustle';
import { StartScreen } from '@/game/components/StartScreen';
import { ScenarioScreen } from '@/game/components/ScenarioScreen';
import { ResultsScreen } from '@/game/components/ResultsScreen';
import { HustleScreen } from '@/game/hustle/HustleScreen';
import { HustleResults } from '@/game/hustle/HustleResults';
import { BchLogo } from '@/game/components/BchLogo';
import { ThemeToggle } from '@/components/ThemeToggle';

type ActiveGame = "none" | "survival" | "hustle";

const Index = () => {
  useSeoMeta({
    title: 'Survive on BCH — A P2P Cash Survival Game',
    description:
      'Can you survive 7 days of everyday spending using Bitcoin Cash — or run a shop and choose how to get paid? A fun, simulated game comparing BCH vs BTC. No real money involved.',
  });

  const [active, setActive] = useState<ActiveGame>("none");
  const game = useGame();
  const hustle = useHustle();

  // Count each unique visit once per browser session.
  useEffect(() => {
    trackVisitOnce();
  }, []);

  // Count a completed game once, when either mode reaches its finished phase.
  useEffect(() => {
    if (active === "survival" && game.phase === "finished") {
      track("game_complete");
    }
  }, [active, game.phase]);

  useEffect(() => {
    if (active === "hustle" && hustle.phase === "finished") {
      track("game_complete");
    }
  }, [active, hustle.phase]);

  function startSurvival(mode: "BCH" | "BTC") {
    game.start(mode);
    setActive("survival");
    track("survival_start", { mode });
  }

  function startHustle() {
    hustle.restart();
    setActive("hustle");
    track("hustle_start");
  }

  function backToStart() {
    setActive("none");
  }

  return (
    <div className="min-h-screen bg-background bch-glow">
      {/* App bar */}
      <header className="sticky top-0 z-10 border-b border-border/70 bg-background/80 backdrop-blur-md">
        <div className="mx-auto max-w-md flex items-center gap-2 px-4 py-3">
          <BchLogo className="w-8 h-8" />
          <span className="font-display font-bold text-foreground">
            Survive on <span className="text-primary">BCH</span>
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1">
            {active === "hustle" ? "Hustle" : active === "survival" ? "Survival" : "Simulated"}
          </span>
          <ThemeToggle className="-mr-2" />
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 py-6 pb-16">
        {active === "none" && (
          <StartScreen
            onStart={startSurvival}
            onStartHustle={startHustle}
            markets={game.markets}
            priceStatus={game.priceStatus}
          />
        )}

        {active === "survival" && game.phase === 'playing' && game.currentScenario && (
          <ScenarioScreen
            scenario={game.currentScenario}
            mode={game.mode}
            run={game.primaryRun}
            markets={game.markets}
            priceStatus={game.priceStatus}
            progress={game.progress}
            onChoose={game.choose}
          />
        )}

        {active === "survival" && game.phase === 'finished' && (
          <ResultsScreen
            mode={game.mode}
            primary={game.primaryRun}
            bch={game.bch}
            btc={game.btc}
            onRestart={backToStart}
          />
        )}

        {active === "hustle" && hustle.phase === 'playing' && hustle.currentCustomer && hustle.currentConditions && (
          <HustleScreen
            customer={hustle.currentCustomer}
            conditions={hustle.currentConditions}
            result={hustle.result}
            progress={hustle.progress}
            onAccept={hustle.accept}
          />
        )}

        {active === "hustle" && hustle.phase === 'finished' && (
          <HustleResults result={hustle.result} onRestart={backToStart} />
        )}
      </main>

      <footer className="mx-auto max-w-md px-4 pb-8 text-center">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </div>
  );
};

export default Index;
